import type { SceneCamera, Site } from "../../types/scene";
import { logger } from "../../utils/logger";
import { formatMjcfNumber } from "./xmlGenerationHelpers";

type ExportBodyNode = {
  uuid?: unknown;
  name?: string;
  children?: ExportBodyNode[];
  sites?: unknown[];
};

type ViewportExportCamera = {
  uuid?: SceneCamera["uuid"];
  name: string;
  kind: "fixed";
  position: number[];
  orientation: number[];
  fovy: number;
  mountType?: SceneCamera["mountType"];
  mountSiteUuid?: SceneCamera["mountSiteUuid"];
  trackingMode?: SceneCamera["trackingMode"];
  targetBodyUuid?: SceneCamera["targetBodyUuid"];
  targetBodyName?: SceneCamera["targetBodyName"];
  attrs?: SceneCamera["attrs"];
};

type ExportCamera = SceneCamera | ViewportExportCamera;

type CameraSiteXmlHelpers = {
  arrayToString: (values: Array<number | string>) => string;
  colorToRgba: (color: unknown) => [number, number, number, number] | null;
  findSiteByIdentifier: (identifier: string | undefined | null) => Site | null;
  normalizeQuat: (values: unknown) => number[];
  quatMultiply: (a: number[], b: number[]) => number[];
  quatRotateVec: (qRaw: number[], vRaw: number[]) => number[];
  sanitizeMjcfName: (value: string) => string;
  toMjcfQuatWxyz: (values: unknown) => number[];
  viewerQuatToMujoco: (qRaw: number[]) => number[];
  viewerVecToMujoco: (vRaw: number[]) => number[];
  xmlEscapeAttr: (value: string) => string;
};

const RESERVED_CAMERA_ATTRS = new Set([
  "name",
  "class",
  "pos",
  "quat",
  "euler",
  "axisangle",
  "xyaxes",
  "zaxis",
  "fovy",
  "mode",
  "targetbody",
  "target",
]);

const CAMERA_ATTR_ORDER = [
  "orthographic",
  "ipd",
  "resolution",
  "focal",
  "focalpixel",
  "principal",
  "principalpixel",
  "sensorsize",
];

const getSiteSizeValues = (site: Site, xmlSiteName: string, logLabel: string): number[] => {
  const rawSize = Array.isArray(site.size) && site.size.length ? site.size : [0.02, 0.02, 0.02];
  let sizeHadInvalid = false;
  const sizeValues = rawSize.map((value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric) || numeric <= 0) {
      sizeHadInvalid = true;
      return 0.02;
    }
    return numeric;
  });
  if (sizeHadInvalid) {
    logger.warn(
      `[xmlGenerationActions] ${logLabel} "${xmlSiteName}" size has non-finite or non-positive elements; invalid values replaced with 0.02.`
    );
  }
  return sizeValues;
};

const appendSiteAttributes = ({
  xml,
  site,
  xmlSiteName,
  logLabel,
  materialUuidToName,
  helpers,
}: {
  xml: string;
  site: Site;
  xmlSiteName: string;
  logLabel: string;
  materialUuidToName: Record<string, string>;
  helpers: CameraSiteXmlHelpers;
}): string => {
  const { arrayToString, colorToRgba, toMjcfQuatWxyz, xmlEscapeAttr } = helpers;
  if (Array.isArray(site.position) && site.position.some((value) => value !== 0)) {
    const pos = site.position.map(Number);
    if (pos.every(Number.isFinite)) {
      xml += ` pos="${arrayToString(pos)}"`;
    } else {
      logger.warn(
        `[xmlGenerationActions] ${logLabel} "${xmlSiteName}" position contains non-finite values; attribute omitted.`
      );
    }
  }
  if (Array.isArray(site.orientation)) {
    const normalized = toMjcfQuatWxyz(site.orientation);
    if (
      !(normalized[0] === 1 && normalized[1] === 0 && normalized[2] === 0 && normalized[3] === 0)
    ) {
      xml += ` quat="${arrayToString(normalized)}"`;
    }
  }
  if (
    site.kind &&
    site.kind !== "sphere" &&
    ["capsule", "ellipsoid", "cylinder", "box"].includes(site.kind)
  ) {
    xml += ` type="${site.kind}"`;
  }
  const siteMaterial =
    typeof site.material === "string" && site.material.trim()
      ? materialUuidToName[site.material.trim()] || site.material.trim()
      : "";
  if (siteMaterial) {
    xml += ` material="${xmlEscapeAttr(siteMaterial)}"`;
  }
  const shouldEmitSiteRgba = !siteMaterial || site.hasExplicitRgba === true;
  if (shouldEmitSiteRgba) {
    const siteRgba = colorToRgba(site.color);
    if (siteRgba) {
      xml += ` rgba="${arrayToString(siteRgba)}"`;
    }
  }
  return xml;
};

const appendCameraAttrs = ({
  xml,
  attrs,
  xmlEscapeAttr,
}: {
  xml: string;
  attrs: SceneCamera["attrs"] | ViewportExportCamera["attrs"] | undefined;
  xmlEscapeAttr: (value: string) => string;
}): string => {
  const cameraAttrs = (attrs && typeof attrs === "object" ? attrs : {}) as Record<string, string>;
  Object.entries(cameraAttrs)
    .filter(([key, value]) => {
      if (!key || value == null) return false;
      const normalizedKey = String(key).trim().toLowerCase();
      return !!normalizedKey && !RESERVED_CAMERA_ATTRS.has(normalizedKey);
    })
    .sort(([keyA], [keyB]) => {
      const normalizedA = String(keyA).trim().toLowerCase();
      const normalizedB = String(keyB).trim().toLowerCase();
      const indexA = CAMERA_ATTR_ORDER.indexOf(normalizedA);
      const indexB = CAMERA_ATTR_ORDER.indexOf(normalizedB);
      if (indexA !== -1 || indexB !== -1) {
        return (
          (indexA === -1 ? Number.MAX_SAFE_INTEGER : indexA) -
          (indexB === -1 ? Number.MAX_SAFE_INTEGER : indexB)
        );
      }
      return normalizedA.localeCompare(normalizedB);
    })
    .forEach(([key, value]) => {
      xml += ` ${key}="${xmlEscapeAttr(String(value))}"`;
    });
  return xml;
};

const appendCameraTargetAttrs = ({
  xml,
  cam,
  bodyUuidToXmlName,
  xmlEscapeAttr,
}: {
  xml: string;
  cam: ExportCamera;
  bodyUuidToXmlName: Record<string, string>;
  xmlEscapeAttr: (value: string) => string;
}): string => {
  const fovy = Number(cam?.fovy);
  if (Number.isFinite(fovy)) {
    xml += ` fovy="${formatMjcfNumber(Math.max(10, Math.min(150, fovy)))}"`;
  }
  if (typeof cam?.trackingMode === "string" && cam.trackingMode.trim().length > 0) {
    xml += ` mode="${xmlEscapeAttr(cam.trackingMode.trim())}"`;
  }
  const targetBodyRef =
    typeof cam?.targetBodyUuid === "string" && bodyUuidToXmlName[String(cam.targetBodyUuid)]
      ? bodyUuidToXmlName[String(cam.targetBodyUuid)]
      : typeof cam?.targetBodyName === "string" && cam.targetBodyName.trim().length > 0
        ? cam.targetBodyName.trim()
        : "";
  if (targetBodyRef) {
    xml += ` targetbody="${xmlEscapeAttr(targetBodyRef)}"`;
  }
  return appendCameraAttrs({ xml, attrs: cam?.attrs, xmlEscapeAttr });
};

export const emitBodySitesXml = ({
  body,
  indent,
  siteExportNameByUuid,
  materialUuidToName,
  helpers,
}: {
  body: ExportBodyNode;
  indent: string;
  siteExportNameByUuid: Record<string, string>;
  materialUuidToName: Record<string, string>;
  helpers: CameraSiteXmlHelpers;
}): string => {
  const { arrayToString, findSiteByIdentifier, sanitizeMjcfName } = helpers;
  let xml = "";
  if (!Array.isArray(body.sites)) return xml;

  body.sites.forEach((siteUuid) => {
    const site = findSiteByIdentifier(String(siteUuid));
    if (!site) return;
    const xmlSiteName =
      (site?.uuid && siteExportNameByUuid[String(site.uuid)]) ||
      sanitizeMjcfName(site.uuid || site.name);
    const sizeValues = getSiteSizeValues(site, xmlSiteName, "Site");
    let line = `${indent}  <site name="${xmlSiteName}" size="${arrayToString(sizeValues)}"`;
    line = appendSiteAttributes({
      xml: line,
      site,
      xmlSiteName,
      logLabel: "Site",
      materialUuidToName,
      helpers,
    });
    xml += `${line}/>\n`;
  });

  return xml;
};

export const emitMountedCamerasXml = ({
  indent,
  mountedCameras,
  cameraExportNameByUuid,
  bodyUuidToXmlName,
  helpers,
}: {
  body: ExportBodyNode;
  indent: string;
  mountedCameras: ExportCamera[];
  cameraExportNameByUuid: Record<string, string>;
  bodyUuidToXmlName: Record<string, string>;
  helpers: CameraSiteXmlHelpers;
}): string => {
  const {
    arrayToString,
    findSiteByIdentifier,
    normalizeQuat,
    quatMultiply,
    quatRotateVec,
    sanitizeMjcfName,
    xmlEscapeAttr,
  } = helpers;
  let xml = "";

  mountedCameras.forEach((cam) => {
    const exportName =
      (cam?.uuid && cameraExportNameByUuid[String(cam.uuid)]) ||
      sanitizeMjcfName(cam?.name || cam?.uuid || "camera");
    const rawCamPos =
      Array.isArray(cam?.position) && cam.position.length === 3 ? cam.position : null;
    if (rawCamPos && rawCamPos.some((value) => !Number.isFinite(Number(value)))) {
      logger.warn(
        `[xmlGenerationActions] Camera "${exportName}" position contains non-finite values; non-finite elements set to 0.`
      );
    }
    const localPos = rawCamPos
      ? rawCamPos.map((value) => {
          const numeric = Number(value);
          return Number.isFinite(numeric) ? numeric : 0;
        })
      : [0, 0, 0];
    let localQuat =
      Array.isArray(cam?.orientation) && cam.orientation.length === 4
        ? normalizeQuat(cam.orientation)
        : [1, 0, 0, 0];
    let posToEmit = localPos;
    if (cam.mountType === "site" && cam.mountSiteUuid) {
      const site = findSiteByIdentifier(String(cam.mountSiteUuid));
      if (site) {
        const sitePos =
          Array.isArray(site.position) && site.position.length === 3
            ? site.position.map((value) => Number(value) || 0)
            : [0, 0, 0];
        const siteQuat =
          Array.isArray(site.orientation) && site.orientation.length === 4
            ? normalizeQuat(site.orientation)
            : [1, 0, 0, 0];
        const rotated = quatRotateVec(siteQuat, localPos);
        posToEmit = [
          Number(sitePos[0]) + Number(rotated[0]),
          Number(sitePos[1]) + Number(rotated[1]),
          Number(sitePos[2]) + Number(rotated[2]),
        ];
        localQuat = quatMultiply(siteQuat, localQuat);
      }
    }

    let line = `${indent}  <camera name="${exportName}" pos="${arrayToString(posToEmit)}"`;
    line += ` quat="${arrayToString(normalizeQuat(localQuat))}"`;
    line = appendCameraTargetAttrs({
      xml: line,
      cam,
      bodyUuidToXmlName,
      xmlEscapeAttr,
    });
    xml += `${line}/>\n`;
  });

  return xml;
};

export const emitFixedCamerasXml = ({
  cameraValuesForExport,
  cameraExportNameByUuid,
  viewportExportCameraName,
  bodyUuidToXmlName,
  helpers,
}: {
  cameraValuesForExport: ExportCamera[];
  cameraExportNameByUuid: Record<string, string>;
  viewportExportCameraName: string;
  bodyUuidToXmlName: Record<string, string>;
  helpers: CameraSiteXmlHelpers;
}): string => {
  const {
    arrayToString,
    normalizeQuat,
    sanitizeMjcfName,
    viewerQuatToMujoco,
    viewerVecToMujoco,
    xmlEscapeAttr,
  } = helpers;
  let xml = "";

  cameraValuesForExport.forEach((cam) => {
    if (!cam || cam.kind !== "fixed") return;
    const exportName =
      (cam?.uuid && cameraExportNameByUuid[String(cam.uuid)]) ||
      cameraExportNameByUuid[viewportExportCameraName] ||
      sanitizeMjcfName(cam?.name || cam?.uuid || "camera");
    const viewerPos =
      Array.isArray(cam.position) && cam.position.length === 3
        ? cam.position.map((value) => Number(value) || 0)
        : [0, 0, 0];
    const viewerQuat =
      Array.isArray(cam.orientation) && cam.orientation.length === 4
        ? normalizeQuat(cam.orientation)
        : [1, 0, 0, 0];
    const pos = viewerVecToMujoco(viewerPos);
    const quat = viewerQuatToMujoco(viewerQuat);
    let line = `    <camera name="${exportName}" pos="${arrayToString(pos)}"`;
    line += ` quat="${arrayToString(normalizeQuat(quat))}"`;
    line = appendCameraTargetAttrs({
      xml: line,
      cam,
      bodyUuidToXmlName,
      xmlEscapeAttr,
    });
    xml += `${line}/>\n`;
  });

  return xml;
};

export const emitWorldbodySitesXml = ({
  bodyTree,
  siteMap,
  siteExportNameByUuid,
  materialUuidToName,
  helpers,
}: {
  bodyTree: ExportBodyNode[];
  siteMap: Record<string, Site>;
  siteExportNameByUuid: Record<string, string>;
  materialUuidToName: Record<string, string>;
  helpers: CameraSiteXmlHelpers;
}): string => {
  const { arrayToString, sanitizeMjcfName } = helpers;
  const siteUuidsOnBodies = new Set<string>();
  const walk = (node: ExportBodyNode): void => {
    if (!node) return;
    if (Array.isArray(node.sites)) {
      node.sites.forEach((ref) => {
        const id = String(ref || "");
        if (id) siteUuidsOnBodies.add(String(id));
      });
    }
    node.children?.forEach(walk);
  };
  bodyTree.forEach(walk);

  let xml = "";
  Object.values(siteMap || {})
    .sort((siteA, siteB) => {
      const nameA =
        (siteA?.uuid && siteExportNameByUuid[String(siteA.uuid)]) ||
        sanitizeMjcfName(siteA?.uuid || siteA?.name || "");
      const nameB =
        (siteB?.uuid && siteExportNameByUuid[String(siteB.uuid)]) ||
        sanitizeMjcfName(siteB?.uuid || siteB?.name || "");
      return nameA.localeCompare(nameB);
    })
    .forEach((site) => {
      if (!site?.uuid) return;
      const uuid = String(site.uuid);
      if (siteUuidsOnBodies.has(uuid)) return;
      const xmlSiteName =
        (site?.uuid && siteExportNameByUuid[String(site.uuid)]) ||
        sanitizeMjcfName(site.uuid || site.name);
      const sizeValues = getSiteSizeValues(site, xmlSiteName, "Worldbody site");
      let line = `    <site name="${xmlSiteName}" size="${arrayToString(sizeValues)}"`;
      line = appendSiteAttributes({
        xml: line,
        site,
        xmlSiteName,
        logLabel: "Worldbody site",
        materialUuidToName,
        helpers,
      });
      xml += `${line}/>\n`;
    });

  return xml;
};
